import getGDriveFileProviderItems from './getGDriveFileProviderItems';
import { defaultSort } from '../constants';
import { canCallFileProviderAPIFromClient } from '../getFileProviderItems/canCallFileProviderAPIFromClient';
import getBoxFileProviderItems from '../getFileProviderItems/getBoxFileProviderItems';
import getDropboxFileProviderItems from '../getFileProviderItems/getDropboxFileProviderItems';
import getFacebookFileProviderItems from '../getFileProviderItems/getFacebookFileProviderItems';
import getMailMessageFileProviderItems from '../getFileProviderItems/getMailMessageFileProviderItems';
import { getOneDriveProFileProviderItems } from '../getFileProviderItems/getOneDriveFileProviderItems';
import { OneDriveProItemsSkipTokenPagingMetadata } from '../types/OneDriveProItemsSkipTokenPagingMetadata';
import { getGDriveQueryString } from '../utils/getGDriveQueryString';
import { getOneDriveProScopeType } from '../utils/getOneDriveProScopeType';
import { isOneDriveProItemsSkipTokenPagingMetadata } from '../utils/typeGuards';
import { logUsage } from 'owa-analytics';
import AttachmentDataProviderScope from 'owa-service/lib/contract/AttachmentDataProviderScope';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentItemsFilterBy from 'owa-service/lib/contract/AttachmentItemsFilterBy';
import AttachmentItemsPagingMetadata from 'owa-service/lib/contract/AttachmentItemsPagingMetadata';
import AttachmentItemsSort from 'owa-service/lib/contract/AttachmentItemsSort';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import GetAttachmentDataProviderItemsRequest from 'owa-service/lib/contract/GetAttachmentDataProviderItemsRequest';
import GetAttachmentDataProviderItemsResponse from 'owa-service/lib/contract/GetAttachmentDataProviderItemsResponse';
import OneDriveProScope from 'owa-service/lib/contract/OneDriveProScope';
import OneDriveProScopeType from 'owa-service/lib/contract/OneDriveProScopeType';
import attachmentItemsPagingDetails from 'owa-service/lib/factory/attachmentItemsPagingDetails';
import indexedPageView from 'owa-service/lib/factory/indexedPageView';
import oneDriveProScope from 'owa-service/lib/factory/oneDriveProScope';
import getAttachmentDataProviderItemsOperation from 'owa-service/lib/operation/getAttachmentDataProviderItemsOperation';
import { trace } from 'owa-trace';

const defaultScope: OneDriveProScope = oneDriveProScope({
    Type: OneDriveProScopeType.MyDocuments,
    DisplayName: 'Files',
    AriaLabel: 'Files',
});

export function callServiceToGetFileProviderItems(
    fileProviderId: string,
    offset: number,
    filterBy: AttachmentItemsFilterBy,
    folderId?: string,
    itemsEndpointUrl?: string,
    location?: string,
    scope?: AttachmentDataProviderScope,
    pagingMetadata?: AttachmentItemsPagingMetadata,
    sort?: AttachmentItemsSort
): Promise<GetAttachmentDataProviderItemsResponse> {
    const request: GetAttachmentDataProviderItemsRequest = {
        AttachmentDataProviderId: fileProviderId,
        Paging: attachmentItemsPagingDetails({
            Sort: sort || defaultSort,
            FolderId: folderId || '',
            Location: location || '',
            ItemsEndpointUrl: itemsEndpointUrl || '',
            FilterBy: filterBy,
            RequestedData: indexedPageView({
                BasePoint: 'Beginning',
                Offset: offset,
                MaxEntriesReturned: 100,
            }),
            PagingMetadata: pagingMetadata,
        }),
        Scope: scope == null ? defaultScope : scope,
    };

    return getAttachmentDataProviderItemsOperation({ request: request });
}

export async function getFileProviderItems(
    fileProviderType: AttachmentDataProviderType,
    fileProviderId: string,
    offset: number,
    filterBy: AttachmentItemsFilterBy,
    scope: AttachmentDataProviderScope = null,
    fileProviderEndpointUrl: string = null,
    folderId?: string,
    location?: string,
    pagingMetadata: AttachmentItemsPagingMetadata = null,
    sort?: AttachmentItemsSort,
    ItemsEndpointUrl: string = null
): Promise<GetAttachmentDataProviderItemsResponse> {
    try {
        const canCallAPIFromClient = await canCallFileProviderAPIFromClient(
            fileProviderType,
            fileProviderEndpointUrl,
            scope
        );

        // Each client call is in a try with no catch so that if the call errors we will not set
        // that we have a client response, and will simply continue on to make the server call instead (after logging the failure).
        let clientCallResponse: GetAttachmentDataProviderItemsResponse;
        let clientCallMade: boolean = false;
        if (canCallAPIFromClient) {
            if (fileProviderType === AttachmentDataProviderType.GDrive) {
                clientCallMade = true;
                // We do not bother wrapping GDrive in its own try-catch as there is no server call for GDrive
                // So we should use the outer try-catch block instead
                clientCallResponse = await getGDriveFileProviderItems(
                    getGDriveQueryString(location, sort)
                );
            } else if (fileProviderType === AttachmentDataProviderType.Dropbox) {
                const sortColumn = sort ? sort.SortColumn : defaultSort.SortColumn;
                const sortOrder = sort ? sort.SortOrder : defaultSort.SortOrder;
                clientCallMade = true;
                try {
                    clientCallResponse = await getDropboxFileProviderItems(
                        location,
                        sortColumn,
                        sortOrder
                    );
                } catch {}
            } else if (fileProviderType === AttachmentDataProviderType.Box) {
                const sortColumn = sort ? sort.SortColumn : defaultSort.SortColumn;
                const sortOrder = sort ? sort.SortOrder : defaultSort.SortOrder;
                clientCallMade = true;
                try {
                    clientCallResponse = await getBoxFileProviderItems(
                        folderId,
                        sortColumn,
                        sortOrder
                    );
                } catch {}
            } else if (fileProviderType === AttachmentDataProviderType.Facebook) {
                clientCallMade = true;
                try {
                    clientCallResponse = await getFacebookFileProviderItems(scope);
                } catch {}
            } else if (fileProviderType === AttachmentDataProviderType.MailMessage) {
                clientCallMade = true;
                // We do not bother wrapping email picker in its own try-catch as there is no server call for emails
                // So we should use the outer try-catch block instead
                clientCallResponse = await getMailMessageFileProviderItems(folderId, offset);
            }
        }

        if (getOneDriveProScopeType(fileProviderType, scope) === OneDriveProScopeType.MyDocuments) {
            if (pagingMetadata && !isOneDriveProItemsSkipTokenPagingMetadata(pagingMetadata)) {
                throw new Error(
                    'The paging metadata is not of type OneDriveProItemsSkipTokenPagingMetadata'
                );
            }

            // We are calling client side call for ODB if the flight is turned on
            // on, the provider is OneDrivePro and the scope is either null (in which
            // case we will use the 'Files' scope by default) or the scope is 'Files'
            clientCallMade = canCallAPIFromClient;
            try {
                clientCallResponse = await getOneDriveProFileProviderItems(
                    fileProviderEndpointUrl,
                    fileProviderId,
                    sort ? sort.SortColumn : defaultSort.SortColumn,
                    sort ? sort.SortOrder : defaultSort.SortOrder,
                    folderId,
                    {
                        metadata: pagingMetadata as OneDriveProItemsSkipTokenPagingMetadata,
                        maxEntries: 100,
                    }
                );
            } catch {}
        }

        if (clientCallResponse && clientCallResponse.ResultCode === AttachmentResultCode.Success) {
            logUsage('FPgetItemsClientCall', [true /* success */, fileProviderType]);
            return clientCallResponse;
        } else if (clientCallMade) {
            logUsage('FPgetItemsClientCall', [false /* success */, fileProviderType]);
        }

        // If we can't make the API call from client, the client call failed, or there is no client side
        // API supported for the provider then make the call to server to get items
        return await callServiceToGetFileProviderItems(
            fileProviderId,
            offset,
            filterBy,
            folderId,
            ItemsEndpointUrl,
            location,
            scope,
            pagingMetadata,
            sort
        );
    } catch (error) {
        trace.warn('FilePicker: getFileProviderItems got error: ' + error.message);
        throw error;
    }
}
